// Centralized date/time utilities for the app
// All DateTimes from the API are stored as UTC in the database

/**
 * Ensures a datetime string has a UTC indicator (Z suffix)
 * The backend stores all datetimes as UTC, but may not always return them with Z suffix
 * @param {string} dateTimeString - DateTime string from API
 * @returns {string} DateTime string with UTC indicator
 */
export const ensureUTCString = (dateTimeString) => {
  if (!dateTimeString) return null;
  // If it already has Z or timezone offset, return as-is
  if (dateTimeString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateTimeString)) {
    return dateTimeString;
  }
  // Add Z to indicate UTC (backend stores all times as UTC)
  return dateTimeString + 'Z';
};

/**
 * Parse a datetime string from the API into a JavaScript Date object
 * Ensures proper UTC handling so times display in user's local timezone
 * @param {string} dateTimeString - DateTime string from API
 * @returns {Date} JavaScript Date object (will display in local timezone)
 */
export const parseUTCDate = (dateTimeString) => {
  const utcString = ensureUTCString(dateTimeString);
  return new Date(utcString);
};

// Chrome on Windows ignores CLDR and follows the OS clock setting,
// so hour12:true/false is unreliable. hourCycle takes absolute precedence.
// 'h12' = 1–12 AM/PM, 'h23' = 0–23 no AM/PM.
// Add new locales here when expanding the app's language support.
const LOCALE_HOUR_CYCLE = {
  en: 'h12',
  he: 'h23',
  bg: 'h23',
};

/**
 * Returns the hourCycle value ('h12' or 'h23') for a locale.
 * Use as: toLocaleString(locale, { ..., hourCycle: getHourCycle(locale) })
 * @param {string} locale - Short locale code (e.g. 'en', 'he', 'bg')
 * @returns {'h12'|'h23'}
 */
export const getHourCycle = (locale) => LOCALE_HOUR_CYCLE[locale] ?? 'h23';

/**
 * Format date as DD/MM/YYYY (or locale-appropriate short date) in user's local timezone
 * @param {string} dateTimeString - DateTime string from API
 * @param {string} locale - BCP 47 locale string (e.g. 'en', 'he', 'bg')
 * @param {string} fallback - Value to return when dateTimeString is null/undefined
 * @returns {string} Formatted date string
 */
export const formatDate = (dateTimeString, locale = 'en', fallback = 'Ongoing') => {
  if (!dateTimeString) return fallback;
  const date = parseUTCDate(dateTimeString);
  return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Format date with weekday and short month (e.g. "Mon, Jan 5") in user's local timezone
 * Used in sidebar and compact date displays
 * @param {string} dateTimeString - DateTime string from API
 * @param {string} locale - BCP 47 locale string
 * @returns {string} Formatted long date string
 */
export const formatDateLong = (dateTimeString, locale = 'en') => {
  if (!dateTimeString) return '';
  const date = parseUTCDate(dateTimeString);
  return date.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
};

/**
 * Format time as HH:MM in user's local timezone
 * Returns empty string for midnight (00:00) — callers treat empty as "no specific time"
 * @param {string} dateTimeString - DateTime string from API
 * @param {string} locale - BCP 47 locale string
 * @returns {string} Formatted time string, or empty string if midnight
 */
export const formatTime = (dateTimeString, locale = 'en') => {
  if (!dateTimeString) return '';
  const date = parseUTCDate(dateTimeString);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  if (hours === 0 && minutes === 0) return '';
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
};

// An event is considered past when it was manually ended OR 2 hours after its scheduled date-time.
export const isEventPast = (dateTimeString, isEnded = false) => {
  if (isEnded) return true;
  if (!dateTimeString) return false;
  return parseUTCDate(dateTimeString).getTime() + 2 * 60 * 60 * 1000 <= Date.now();
};